import { Component } from '@theme/component';

/**
 * Yaslie — calculateur de taille.
 *
 * Deux mesures → une taille. Les règles viennent de la fiche fournisseur
 * (product-data.md) :
 *
 *  1. les plages de sous-poitrine se chevauchent volontairement (75B tombe
 *     en M et en L) → en cas de doute, on recommande la PLUS GRANDE ;
 *  2. les plages de tour de poitrine laissent des trous (85 → 87, 90 → 92…) ;
 *     une mesure qui tombe dans un trou est arrondie à la taille AU-DESSUS ;
 *  3. au-delà de la dernière ligne du tableau, on n'extrapole pas : les
 *     mesures 4XL/5XL ne sont pas connues, inventer une correspondance sur un
 *     vêtement porté à même la peau serait un mauvais service.
 */
class YaslieSizeCalculator extends Component {
  /** @type {{label: string, under: [number, number], bust: [number, number]}[]} */
  #table = [];

  connectedCallback() {
    super.connectedCallback();

    this.#table = this.#parseTable();
    if (!this.#table.length) {
      console.warn('[yaslie-size-calculator] tableau des tailles vide ou illisible');
      return;
    }

    this.querySelector('[data-yaslie-sizer-submit]')?.addEventListener('click', this.#handleSubmit);

    for (const input of this.querySelectorAll('input[type="number"]')) {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.#handleSubmit();
        }
      });
    }
  }

  /**
   * Lit le réglage « une taille par ligne » : TAILLE|min-max|min-max.
   * Les lignes mal formées sont ignorées plutôt que de casser le bloc.
   */
  #parseTable() {
    const raw = this.querySelector('[data-yaslie-sizer-table]')?.textContent;
    if (!raw) return [];

    /** @type {string} */
    let text;
    try {
      text = JSON.parse(raw);
    } catch {
      return [];
    }

    const parseRange = (value) => {
      const [min, max] = String(value).split('-').map((part) => Number(part.trim()));
      return Number.isFinite(min) && Number.isFinite(max) ? [min, max] : null;
    };

    return String(text)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, under, bust] = line.split('|').map((part) => part?.trim());
        const underRange = under ? parseRange(under) : null;
        const bustRange = bust ? parseRange(bust) : null;
        if (!label || !underRange || !bustRange) return null;
        return { label, under: underRange, bust: bustRange };
      })
      .filter(Boolean);
  }

  /**
   * Index de la première taille (par ordre croissant) dont la borne haute
   * couvre la mesure. C'est ce qui produit l'arrondi à la taille au-dessus.
   * @param {number} value
   * @param {'under'|'bust'} key
   * @returns {number} -1 si la mesure dépasse le tableau
   */
  #indexFor(value, key) {
    return this.#table.findIndex((size) => value <= size[key][1]);
  }

  #handleSubmit = () => {
    const underbust = Number(this.querySelector('[data-yaslie-underbust]')?.value);
    const bust = Number(this.querySelector('[data-yaslie-bust]')?.value);

    if (!Number.isFinite(underbust) || !Number.isFinite(bust) || underbust <= 0 || bust <= 0) {
      this.#renderError('Indiquez vos deux mesures en centimètres.');
      return;
    }

    // Le tour de poitrine passe par-dessus la poitrine, il est nécessairement
    // supérieur au tour de sous-poitrine. Inversion = mesures interverties.
    if (bust <= underbust) {
      this.#renderError(
        'Le tour de poitrine doit être supérieur au tour de sous-poitrine. Vérifiez que les deux mesures ne sont pas inversées.'
      );
      return;
    }

    const underIndex = this.#indexFor(underbust, 'under');
    const bustIndex = this.#indexFor(bust, 'bust');

    if (underIndex === -1 || bustIndex === -1) {
      this.#renderOutside();
      return;
    }

    // Règle du doute : on retient toujours la plus grande des deux lectures.
    const index = Math.max(underIndex, bustIndex);
    const size = this.#table[index];

    const notes = [];

    if (underIndex !== bustIndex) {
      const smaller = this.#table[Math.min(underIndex, bustIndex)].label;
      notes.push(
        `Vos deux mesures ne pointent pas la même taille (${smaller} et ${size.label}). Nous retenons la plus grande : sur ce modèle, une taille trop juste marque sous la poitrine.`
      );
    }

    const below = bust < size.bust[0] || underbust < size.under[0];
    if (below && index === 0) {
      // Sous la première ligne du tableau : ce n'est pas un arrondi, c'est
      // simplement la plus petite taille que nous produisons.
      notes.push(`Vos mesures sont un peu en dessous de notre plus petite taille ; le ${size.label} reste le plus proche.`);
    } else if (below) {
      notes.push('Vos mesures tombent entre deux tailles. Nous arrondissons à la taille au-dessus.');
    }

    this.#renderMatch(size.label, notes.join(' '));
  };

  /** @returns {HTMLElement | null} */
  get #result() {
    return this.querySelector('[data-yaslie-sizer-result]');
  }

  /** @param {string} name */
  #fill(name) {
    const template = this.querySelector(`[data-yaslie-tpl-${name}]`);
    const result = this.#result;
    if (!(template instanceof HTMLTemplateElement) || !result) return null;

    result.replaceChildren(template.content.cloneNode(true));
    result.hidden = false;
    return result;
  }

  #renderError(message) {
    const result = this.#fill('error');
    const node = result?.querySelector('[data-message]');
    if (node) node.textContent = message;
  }

  #renderOutside() {
    this.#fill('outside');
  }

  /**
   * @param {string} label
   * @param {string} note
   */
  #renderMatch(label, note) {
    const result = this.#fill('match');
    if (!result) return;

    const sizeNode = result.querySelector('[data-size]');
    if (sizeNode) sizeNode.textContent = label;

    const noteNode = result.querySelector('[data-note]');
    if (noteNode) {
      noteNode.textContent = note;
      noteNode.hidden = note === '';
    }

    // Le bouton de sélection n'apparaît que si la taille existe vraiment dans
    // le sélecteur de variantes ET qu'il est visible (il ne l'est pas en mode
    // pack, où la sélection se fait exemplaire par exemplaire).
    const input = this.#findVariantInput(label);
    const apply = result.querySelector('[data-yaslie-sizer-apply]');
    if (apply && input) {
      apply.hidden = false;
      apply.addEventListener(
        'click',
        () => {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.closest('variant-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        { once: true }
      );
    }
  }

  /**
   * Cherche le bouton de taille correspondant dans le sélecteur natif.
   * On matche sur la valeur et pas sur le nom d'option, pour survivre au
   * renommage de « Size » en « Taille » dans l'admin.
   * @param {string} label
   */
  #findVariantInput(label) {
    const picker = document.querySelector('variant-picker');
    if (!picker || picker.offsetParent === null) return null;

    return Array.from(picker.querySelectorAll('input[type="radio"]')).find(
      (input) => input.value === label && input.dataset.optionAvailable !== 'false'
    );
  }
}

if (!customElements.get('yaslie-size-calculator')) {
  customElements.define('yaslie-size-calculator', YaslieSizeCalculator);
}
